/**
 * demoDetail — synthetic per-expediente data for the detail screen in DEMO
 * mode (no `VITE_API_BASE_URL`). Mirrors the rows the Tablero's expediente
 * browser mocks (EXP-DEMO-0001…0004) so a click-through works without a
 * backend. Exercises the three QR official-URL states of issue #261 —
 * SUSPECT (0001), IN_REGISTRO (0002), sin QR (0003) — and the ADR 0007
 * single-identification-file case of issue #275 (0004: one file filling both
 * INE slots, with inline SVG documents so the workbench notice is visible).
 */

import type { DocumentRef, ExpedienteDetail } from "../../api/client";

const DEMO_SUSPECT_URL = "https://certificados-sep.valida-mx.com/consulta?f=9d1c";
const DEMO_OFFICIAL_URL =
  "https://www.controlescolar.aefcm.gob.mx/consultas/certificado?folio=3f8a";

type DemoRecord = {
  detail: ExpedienteDetail;
  /** The latest run's `checks` (`layer_summary`) as the history endpoint serves it. */
  checks: Record<string, unknown>;
};

function baseFields(curp: string, nombre: string, hasQr: boolean): Record<string, unknown> {
  return {
    ine_front: {
      curp: { value: curp, legible: true },
      nombre: { value: nombre, legible: true },
      vigencia_inicio: 2021,
      vigencia_fin: 2031,
      document_present: true,
    },
    ine_back: null,
    certificado: {
      curp: { value: curp, legible: true },
      folio: { value: "8f4e2a1c-77d3-4b9e-a1a2-demo00000001", legible: true },
      nombre: { value: nombre, legible: true },
      institucion: { value: "Secundaria Técnica No. 45", legible: true },
      anio_emision: 2025,
      cert_type: hasQr ? "DIGITAL_QR" : "PRINTED_MODERN",
      has_qr: hasQr,
      qr_payload: null,
      sep_seal_present: true,
      legible: true,
    },
  };
}

function layer2Checks(url: string | null, status: string | null): Record<string, unknown> {
  return {
    layer1: { documents_complete: "PASS" },
    layer2: {
      curp_cross_match: "PASS",
      name_comparison: true,
      face_confidence: "HIGH",
      ine_validation: true,
      qr_url: url,
      qr_official_status: status,
    },
  };
}

const DEMO_RECORDS: Record<string, DemoRecord> = {
  // SUSPECT: the QR points off the Registro and off any gob.mx/edu.mx host —
  // carries the QR_DOMINIO_SOSPECHOSO flag the badge must sit next to.
  "EXP-DEMO-0001": {
    detail: {
      expediente_id: "EXP-DEMO-0001",
      round_id: "DEMO",
      automated: {
        expediente_id: "EXP-DEMO-0001",
        decision: "HUMAN_REVIEW",
        timestamp: "2026-06-09T14:21:00Z",
        failures: [],
        flags: [
          {
            code: "QR_DOMINIO_SOSPECHOSO",
            priority: "HIGH",
            detail:
              "El QR del certificado apunta a un dominio sospechoso (fuera del " +
              `Registro de URLs oficiales y no gubernamental/educativo): ${DEMO_SUSPECT_URL}`,
          },
          { code: "FACE_CONFIDENCE_LOW", priority: "HIGH", detail: null },
        ],
        priority: "HIGH",
        confidence_summary: {},
        extracted_fields: baseFields("DEMO010101HDFXXX01", "MARÍA DEMO PÉREZ", true),
        // Demo of the operator tag chip; mirrors RoundExpedientesBrowser.
        tags: ["menor_de_edad"],
        processing_time_seconds: 4.2,
        audit_id: "aud_demo_1",
        adjudicated: false,
      },
      effective_disposition: {
        decision: "HUMAN_REVIEW",
        source: "AUTOMATED",
        latest_adjudication: null,
      },
      channel: "batch",
      submitted_email: null,
    },
    checks: layer2Checks(DEMO_SUSPECT_URL, "SUSPECT"),
  },

  // IN_REGISTRO: official portal, previously flagged for name mismatch and
  // approved by a human (adjudicated example in the browser mock).
  "EXP-DEMO-0002": {
    detail: {
      expediente_id: "EXP-DEMO-0002",
      round_id: "DEMO",
      automated: {
        expediente_id: "EXP-DEMO-0002",
        decision: "HUMAN_REVIEW",
        timestamp: "2026-06-09T14:05:00Z",
        failures: [],
        flags: [{ code: "NAME_MISMATCH", priority: "HIGH", detail: null }],
        priority: "HIGH",
        confidence_summary: {},
        extracted_fields: baseFields("DEMO020202MDFXXX02", "JOSÉ DEMO LÓPEZ", true),
        // Demo de los chips del re-scan QR (PRD #305).
        tags: ["qr_dominio_sospechoso"],
        processing_time_seconds: 3.1,
        audit_id: "aud_demo_2",
        adjudicated: true,
      },
      effective_disposition: {
        decision: "APPROVED",
        source: "ADJUDICATED",
        latest_adjudication: {
          adjudication_id: "adj_demo_1",
          decision: "APPROVED",
          reviewer_email: "revisor@demo.veravalida.com",
          documentos_observados: [],
          actor: null,
          timestamp: "2026-06-09T15:40:00Z",
        },
      },
      channel: "batch",
      submitted_email: null,
    },
    checks: layer2Checks(DEMO_OFFICIAL_URL, "IN_REGISTRO"),
  },

  // Sin QR: the neutral absent state (~58% of the corpus).
  "EXP-DEMO-0003": {
    detail: {
      expediente_id: "EXP-DEMO-0003",
      round_id: "DEMO",
      automated: {
        expediente_id: "EXP-DEMO-0003",
        decision: "APPROVED",
        timestamp: "2026-06-09T13:58:00Z",
        failures: [],
        flags: [],
        priority: null,
        confidence_summary: {
          face_match: "HIGH",
          ine_validation_source: "INTERNAL",
        },
        extracted_fields: baseFields("DEMO030303HDFXXX03", "ANA DEMO GARCÍA", false),
        tags: ["qr_rescan_ilegible"],
        processing_time_seconds: 2.4,
        audit_id: "aud_demo_3",
        adjudicated: false,
      },
      effective_disposition: {
        decision: "APPROVED",
        source: "AUTOMATED",
        latest_adjudication: null,
      },
      channel: "batch",
      submitted_email: null,
    },
    checks: layer2Checks(null, null),
  },

  // Documento de identificación único (ADR 0007 / #275): one file filled both
  // INE slots; its back (MRZ) could not be established → INE validation PENDING.
  "EXP-DEMO-0004": {
    detail: {
      expediente_id: "EXP-DEMO-0004",
      round_id: "DEMO",
      automated: {
        expediente_id: "EXP-DEMO-0004",
        decision: "HUMAN_REVIEW",
        timestamp: "2026-06-09T13:41:00Z",
        failures: [],
        flags: [
          {
            code: "IDEN_VALIDATION_PENDING",
            priority: "NORMAL",
            detail: "Reverso del INE no disponible: MRZ no evaluable",
          },
        ],
        priority: "NORMAL",
        confidence_summary: {},
        extracted_fields: baseFields("DEMO040404HDFXXX04", "LUIS DEMO RAMÍREZ", false),
        tags: [],
        processing_time_seconds: 3.6,
        audit_id: "aud_demo_4",
        adjudicated: false,
      },
      effective_disposition: {
        decision: "HUMAN_REVIEW",
        source: "AUTOMATED",
        latest_adjudication: null,
      },
      channel: "batch",
      submitted_email: null,
    },
    checks: {
      layer1: { documents_complete: "PASS" },
      layer2: {
        curp_cross_match: "PASS",
        name_comparison: true,
        face_confidence: "HIGH",
        ine_validation: null,
        ine_checks: { curp_structure: true, vigency: true, mrz: null },
        qr_url: null,
        qr_official_status: null,
      },
    },
  },
};

// ── DEMO documents (issue #275) ──────────────────────────────────────────────
// Inline SVG data URIs — self-contained, no backend, render in the <img> stage.

function svgDocument(title: string, subtitle: string, accent: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420">` +
    `<rect width="640" height="420" fill="#f4f1ea"/>` +
    `<rect x="24" y="24" width="592" height="372" rx="12" fill="#fff" stroke="${accent}" stroke-width="3"/>` +
    `<rect x="48" y="56" width="140" height="170" rx="8" fill="#e5e0d4"/>` +
    `<text x="220" y="100" font-family="sans-serif" font-size="30" font-weight="bold" fill="#333">${title}</text>` +
    `<text x="220" y="140" font-family="sans-serif" font-size="18" fill="#666">${subtitle}</text>` +
    `<text x="220" y="180" font-family="sans-serif" font-size="14" fill="#999">Documento sintético — modo DEMO</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function demoDoc(
  tipo: string,
  url: string,
  digest: string,
): DocumentRef {
  return {
    tipo,
    object_name: `demo/EXP-DEMO-0004/${tipo}.svg`,
    url,
    content_type: "image/svg+xml",
    expires_at: "2999-12-31T23:59:59+00:00",
    content_digest: digest,
  };
}

// ONE identification file, stored under both INE slots: same url, same digest —
// exactly the shape the API serves after ADR 0007's console-side duplication.
const DEMO_SINGLE_IDEN_URL = svgDocument(
  "Identificación (ambos lados)", "Un solo archivo entregado", "#8a2b4a",
);

const DEMO_DOCUMENTS: Record<string, DocumentRef[]> = {
  "EXP-DEMO-0004": [
    demoDoc("ine", DEMO_SINGLE_IDEN_URL, "demo-digest-iden"),
    demoDoc("ine_reverso", DEMO_SINGLE_IDEN_URL, "demo-digest-iden"),
    demoDoc(
      "certificado",
      svgDocument("Certificado", "Secundaria Técnica No. 45", "#2b5a8a"),
      "demo-digest-cert",
    ),
    demoDoc(
      "foto",
      svgDocument("Fotografía", "Foto reciente del aspirante", "#4a7a3a"),
      "demo-digest-foto",
    ),
  ],
};

/** Synthetic `ExpedienteDetail`, or null for an id the DEMO browser never links. */
export function demoExpedienteDetail(expedienteId: string): ExpedienteDetail | null {
  return DEMO_RECORDS[expedienteId]?.detail ?? null;
}

/** Synthetic documents; empty (the workbench empty state) for the other ids. */
export function demoExpedienteDocuments(expedienteId: string): DocumentRef[] {
  return DEMO_DOCUMENTS[expedienteId] ?? [];
}

/** Synthetic latest-run `checks`; null (absent state) for unknown ids. */
export function demoExpedienteChecks(expedienteId: string): Record<string, unknown> | null {
  return DEMO_RECORDS[expedienteId]?.checks ?? null;
}
